import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { subscribeToApiWrites } from "@/lib/api-write-events";
import { writeAffectsCapacity } from "@/lib/capacity-writes";
import { peekQueryClient } from "./client";
import { rereadOlderThan } from "./reread";

/**
 * How long a tab that wrote waits for the backend's `capacity:list` hint before
 * it re-reads anyway. The rows a capacity view reads are rewritten by a
 * background rebuild after the write returns, so a re-read at once usually
 * fetches the old rows and the hint's re-read fetches them all again. Long
 * enough for the rebuild of an ordinary write; with the hint switched off or
 * the stream down, the view catches up after this.
 */
export const CAPACITY_HINT_WAIT_MS = 2_500;

/**
 * How long the timeline's re-read waits, so a burst of writes (a bulk edit, a
 * save that also announces itself) costs one re-read.
 */
export const SCHEDULE_REREAD_DEBOUNCE_MS = 250;

/**
 * The families a schedule write can change whose rows a background rebuild
 * rewrites, so their re-read waits for the `capacity:list` hint.
 */
const HINTED_DOMAINS: ReadonlySet<unknown> = new Set(["capacity"]);

/**
 * The families a schedule write can change that read the written rows
 * directly: the write has landed by the time they are read, so they re-read
 * at once rather than waiting on a rebuild they do not read.
 */
const DIRECT_DOMAINS: ReadonlySet<unknown> = new Set(["timeline"]);

/** True for a cached read the `capacity:list` hint covers. */
function isHintedRead({ queryKey }: { queryKey: QueryKey }): boolean {
  return HINTED_DOMAINS.has(queryKey[2]);
}

function isDirectRead({ queryKey }: { queryKey: QueryKey }): boolean {
  return DIRECT_DOMAINS.has(queryKey[2]);
}

function rereadScheduleReads(client: QueryClient, since: number, why: string): void {
  rereadOlderThan(client, isHintedRead, since).catch((error: unknown) => {
    console.warn(`[queries] re-reading capacity ${why} failed`, { since, error });
  });
}

/** This tab's pending wait: the latest write it covers, and its deadline. */
let awaiting: { client: QueryClient; since: number; timer: ReturnType<typeof setTimeout> } | null =
  null;

/**
 * This tab made a write that can move a bar: mark every capacity answer stale
 * now, and re-read the ones on screen ONCE, when the `capacity:list` hint for
 * the rebuild arrives (`scheduleHintArrived`) or after `CAPACITY_HINT_WAIT_MS`,
 * whichever comes first (the PR-7210 box test: one reassign used to read every
 * query on screen three or four times, cancelling between).
 *
 * Every workspace and principal: the URL names the workspace, but a stale entry
 * for another one is harmless and a missed one is the bug this exists for. A
 * write while the wait runs joins it; the deadline does not move.
 */
export function awaitScheduleHint(client: QueryClient): void {
  void client.invalidateQueries({ predicate: isHintedRead, refetchType: "none" });
  const now = Date.now();
  if (awaiting) {
    awaiting.since = now;
    return;
  }
  const timer = setTimeout(() => {
    const due = awaiting;
    awaiting = null;
    if (due) rereadScheduleReads(due.client, due.since, "after a write");
  }, CAPACITY_HINT_WAIT_MS);
  awaiting = { client, since: now, timer };
}

/**
 * A `capacity:list` hint arrived at `at`. When this tab is waiting on one for
 * its own write, the re-read runs now, without the jitter that spreads other
 * tabs' reads, and the answer is `true`. Otherwise `false`: the hint row
 * re-reads on the listener's timing.
 */
export function scheduleHintArrived(client: QueryClient, at: number): boolean {
  if (!awaiting || awaiting.client !== client) return false;
  clearTimeout(awaiting.timer);
  awaiting = null;
  rereadScheduleReads(client, at, "on the capacity hint");
  return true;
}

let installed = false;
let directTimer: ReturnType<typeof setTimeout> | null = null;
let lastWriteAt = 0;

/** Re-read the timeline windows read before the burst's last write, once the burst settles. */
function rereadDirectReads(): void {
  lastWriteAt = Date.now();
  if (directTimer !== null) return;
  directTimer = setTimeout(() => {
    directTimer = null;
    const client = peekQueryClient();
    if (!client) return;
    rereadOlderThan(client, isDirectRead, lastWriteAt).catch((error: unknown) => {
      console.warn("[queries] re-reading the timeline after a write failed", { error });
    });
  }, SCHEDULE_REREAD_DEBOUNCE_MS);
}

/**
 * Re-read capacity and the timeline after any write that can move a bar
 * (NG-2812): the timeline once the burst settles, capacity once its rebuild
 * has landed (`awaitScheduleHint`).
 *
 * Capacity changes when work changes anywhere in the app: a work item
 * rescheduled on its own page, a modal over the grid, a bulk edit, someone's
 * hours. The HTTP layer announces every successful write and
 * `writeAffectsCapacity` picks the ones that matter. This replaced the capacity
 * store's per-view listener, which only heard writes while a grid was mounted
 * and marked its reads stale on leaving instead: the cache now hears every
 * write, so a later visit asks again only when something changed.
 *
 * Installed once, from `root.tsx`. It reads the client at the time of the
 * write, so a test's own client is the one invalidated.
 */
export function installScheduleWriteInvalidation(): void {
  if (installed) return;
  installed = true;
  subscribeToApiWrites(({ url }) => {
    if (!writeAffectsCapacity(url)) return;
    rereadDirectReads();
    const client = peekQueryClient();
    if (client) awaitScheduleHint(client);
  });
}
